import React, { useMemo, useState } from 'react';
import {
  dataModelerTablesEditorGridColumns,
  StyledFormUIRow,
} from '../DataModelerCommonUI';
import * as FormUI from '../../../pageForm/FormUI';
import { Order } from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { assertNotNil } from '../../../../utils/types';
import {
  generateSortableColumnClickFunc,
  matchLowerCaseNeedleStr,
} from '../utils';
import SortableHeaderLabel from '../../../pageForm/SortableHeaderLabel';
import {
  dataModelerSelectCurrentDraftEdits,
  dataModelerSelectCurrentSchemaObj,
  dataModelerSelectCurrentUiState,
} from '../../../../redux/selectors/dataModeler';
import { defaultEmptyDraftEdits } from '../../../../redux/slices/dataModeler/dataModelerSlice';
import DataModelerTableRow from './DataModelerTableRow';
import { ISortableTableItem, SortableTablesGridColumnKeys } from './types';
import { useAllTableItems } from './utils';
import { dataModelerToggleExpandTable } from '../../../../redux/slices/dataModeler';
import DataModelerTableExpanded from './DataModelerTableExpanded';
import { UnreachableCaseError } from 'ts-essentials';

interface IPros {
  isEditMode: boolean;
  searchQuery: string;
}

const tableItemMatches = (searchQuery: string, item: ISortableTableItem) => {
  const { tableKey, sqlTableName, viewKey } = item;
  return (
    matchLowerCaseNeedleStr(searchQuery, tableKey) ||
    matchLowerCaseNeedleStr(searchQuery, sqlTableName) ||
    matchLowerCaseNeedleStr(searchQuery, viewKey)
  );
};

const compareItemsFunc = (
  sortColumn: SortableTablesGridColumnKeys,
): ((a: ISortableTableItem, b: ISortableTableItem) => number) => {
  return (a, b) => {
    switch (sortColumn) {
      case 'viewKey':
      case 'tableKey':
      case 'sqlTableName':
        return a[sortColumn].localeCompare(b[sortColumn]);
      case 'modified':
      case 'default':
        return a.isNew < b.isNew ? 1 : a.isModified < b.isModified ? 1 : -1;
      case 'viewType':
        return a.isFactTable < b.isFactTable ? 1 : -1;
      default:
        throw new UnreachableCaseError(sortColumn);
    }
  };
};

const compareItemsWithOrderFunc = (
  sortColumn: SortableTablesGridColumnKeys,
  sortOrder: Order,
): ((a: ISortableTableItem, b: ISortableTableItem) => number) => {
  const compareFunc = compareItemsFunc(sortColumn);
  return (a, b) => {
    const compareRet = compareFunc(a, b);
    return sortOrder === Order.Asc ? compareRet : -compareRet;
  };
};

const DataModelerTablesGrid = ({ isEditMode, searchQuery }: IPros) => {
  const dispatch = useAppDispatch();
  const [sortOrderOrNull, setSortOrderOrNull] = useState<Order | null>(null);
  const [sortColumn, setSortColumn] =
    useState<SortableTablesGridColumnKeys>('default');

  const schemaObj = useAppSelector(dataModelerSelectCurrentSchemaObj);
  assertNotNil(schemaObj);
  const draftEdits =
    useAppSelector(dataModelerSelectCurrentDraftEdits) ??
    defaultEmptyDraftEdits;

  const expandedTableKey =
    useAppSelector(dataModelerSelectCurrentUiState)?.expandedTableKey ?? null;

  const closeExpanded = () => {
    dispatch(dataModelerToggleExpandTable({ tableKey: null }));
  };

  const expandTable = (tableKey: string) => {
    dispatch(dataModelerToggleExpandTable({ tableKey }));
  };

  const allTableItems: ISortableTableItem[] = useAllTableItems({
    schemaObj,
    draftEdits,
  });

  const expandedTableItem = allTableItems.find(
    (tableItem) => tableItem.tableKey === expandedTableKey,
  );

  const sortedAndFilteredTableItems = useMemo(() => {
    const filtered = allTableItems.filter((tableItem) =>
      tableItemMatches(searchQuery, tableItem),
    );
    if (sortOrderOrNull === null) {
      return filtered;
    }
    return filtered.sort(
      compareItemsWithOrderFunc(sortColumn, sortOrderOrNull),
    );
  }, [allTableItems, searchQuery, sortColumn, sortOrderOrNull]);

  const sortOrder = sortOrderOrNull ?? Order.Asc;

  const onColumnClickFn = (forSortColumn: SortableTablesGridColumnKeys) => {
    return generateSortableColumnClickFunc(
      forSortColumn,
      sortColumn,
      setSortColumn,
      setSortOrderOrNull,
    );
  };
  if (expandedTableItem) {
    return (
      <DataModelerTableExpanded
        tableItem={expandedTableItem}
        isEditMode={isEditMode}
        collapse={closeExpanded}
      />
    );
  }
  return (
    <FormUI.Container $noMarginTop={true}>
      <StyledFormUIRow
        $isHeader
        $gridTemplateColumns={dataModelerTablesEditorGridColumns}
      >
        <SortableHeaderLabel
          onClick={onColumnClickFn('viewType')}
          activeSortType={sortOrder}
          text={'Type'}
          isActive={sortColumn === 'viewType'}
          shouldAlignRight={false}
        />
        <SortableHeaderLabel
          onClick={onColumnClickFn('viewKey')}
          activeSortType={sortOrder}
          text={'Table'}
          isActive={sortColumn === 'viewKey'}
          shouldAlignRight={false}
        />
        <SortableHeaderLabel
          onClick={onColumnClickFn('sqlTableName')}
          activeSortType={sortOrder}
          text={'Database Table'}
          isActive={sortColumn === 'sqlTableName'}
          shouldAlignRight={false}
        />
        <SortableHeaderLabel
          onClick={onColumnClickFn('default')}
          activeSortType={sortOrder}
          text={'Modified'}
          iconName="pencil"
          isActive={sortColumn === 'default' && sortOrderOrNull !== null}
          shouldAlignRight={false}
        />
      </StyledFormUIRow>
      {sortedAndFilteredTableItems.map((tableItem, ix) => {
        return (
          <DataModelerTableRow
            key={`${tableItem.tableKey}-${ix}`}
            tableItem={tableItem}
            schemaKey={schemaObj.key}
            expandTable={expandTable}
          />
        );
      })}
    </FormUI.Container>
  );
};

export default DataModelerTablesGrid;
